import React, { useState, useEffect } from 'react';
import TemplatePreviewModal from './TemplatePreviewModal';
import { useTheme } from '../hooks/useTheme';
import SaasLandingPage from '../templates/Snapshots/SaasLandingPage.png';
import eCommSnap from '../templates/Snapshots/eCommSnap.png';
import restaurantSnap from '../templates/Snapshots/restaurantSnap.png';
import taskMgtSnap from '../templates/Snapshots/taskMgtSnap.png';
import portfolioSnap from '../templates/Snapshots/portfolioSnap.png';
import gymSnap from '../templates/Snapshots/gymSnap.png';
import realEstateSnap from '../templates/Snapshots/realEstateSnap.png';
import blogSnap from '../templates/Snapshots/blogSnap.png';
import healthCareSnap from '../templates/Snapshots/healthCareSnap.png';
import adminDashSnap from '../templates/Snapshots/adminDashSnap.png';

const TEMPLATES = [
  {
    id: 'saas-landing',
    type: 'saas-landing',
    emoji: '🚀',
    title: 'SaaS Landing Page',
    desc: 'Hero, features, pricing tiers, and conversion-focused CTAs for modern SaaS products.',
    category: 'Marketing',
    stack: 'React + Tailwind',
    previewImage: SaasLandingPage,
    prompt:
      'Build a modern SaaS landing page with hero section, features section with 3 cards, pricing table with 3 tiers, header with navigation, and footer. Use purple accent colors and clean modern design.',
  },
  {
    id: 'ecommerce-store',
    type: 'ecommerce-store',
    emoji: '🛍️',
    title: 'E-Commerce Store',
    desc: 'Luxury storefront with product grid, categories, and editorial shopping experience.',
    category: 'Commerce',
    stack: 'React + Tailwind',
    previewImage: eCommSnap,
    prompt:
      'Customize this e-commerce storefront template. Edit existing components — product cards, navbar, hero banner, categories, and footer. Do NOT regenerate from scratch.',
  },
  {
    id: 'restaurant-website',
    type: 'restaurant-website',
    emoji: '🍽️',
    title: 'Restaurant Website',
    desc: 'Fine-dining layout with menu highlights, reservations, and gallery sections.',
    category: 'Hospitality',
    stack: 'React + Tailwind',
    previewImage: restaurantSnap,
    prompt:
      'Customize this restaurant website template. Edit existing components — hero, featured dishes, menu showcase, reservations, and gallery. Do NOT regenerate from scratch.',
  },
  {
    id: 'task-management-dashboard',
    type: 'task-management-dashboard',
    emoji: '📋',
    title: 'Task Management Dashboard',
    desc: 'Kanban boards, workload analytics, and team activity in a productivity workspace.',
    category: 'Dashboard',
    stack: 'React + Tailwind',
    previewImage: taskMgtSnap,
    prompt:
      'Customize this task management dashboard template. Edit existing components — Kanban board, analytics widgets, sidebar, and team activity. Do NOT regenerate from scratch.',
  },
  {
    id: 'portfolio-website',
    type: 'portfolio-website',
    emoji: '🎨',
    title: 'Portfolio Website',
    desc: 'Showcase projects, skills, and contact with polished personal-brand styling.',
    category: 'Portfolio',
    stack: 'React + CSS',
    previewImage: portfolioSnap,
    prompt:
      'Create a personal portfolio website with hero section, projects showcase grid, skills section, about me page, and contact form with smooth animations and modern design.',
  },
  {
    id: 'healthcare-dashboard',
    type: 'healthcare-dashboard',
    emoji: '🩺',
    title: 'Healthcare Dashboard',
    desc: 'Clinical operations UI with patient queue, vitals, and care coordination panels.',
    category: 'Healthcare',
    stack: 'React + Tailwind',
    previewImage: healthCareSnap,
    prompt:
      'Customize this healthcare dashboard template. Edit existing components — sidebar, patient table, appointment schedule, medical reports, and notifications. Do NOT regenerate from scratch.',
  },
  {
    id: 'fitness-gym-website',
    type: 'fitness-gym-website',
    emoji: '💪',
    title: 'Fitness/Gym Website',
    desc: 'High-contrast athletic brand site with programs, coaches, and membership tiers.',
    category: 'Fitness',
    stack: 'React + Tailwind',
    previewImage: gymSnap,
    prompt:
      'Customize this fitness/gym website template. Edit existing components — hero, programs, coaches, transformation phases, membership tiers, testimonials, and gallery. Do NOT regenerate from scratch.',
  },
  {
    id: 'real-estate-listing',
    type: 'real-estate-listing',
    emoji: '🏡',
    title: 'Real Estate Listing',
    desc: 'Luxury property discovery with featured listings, search, and agent profiles.',
    category: 'Real Estate',
    stack: 'React + Tailwind',
    previewImage: realEstateSnap,
    prompt:
      'Customize this luxury real estate template. Edit existing components — hero, featured properties, discovery search bar, interiors gallery, neighborhoods, agents, testimonials, and CTA. Do NOT regenerate from scratch.',
  },
  {
    id: 'blog-cms',
    type: 'blog-cms',
    emoji: '📰',
    title: 'Blog/CMS',
    desc: 'Pastel travel journal with editorial stories, destinations grid, and article layouts.',
    category: 'Blog',
    stack: 'React + Tailwind',
    previewImage: blogSnap,
    prompt:
      'Customize this Paloma pastel travel-journal blog template. Edit existing components — minimal navbar with marquee ticker, asymmetric polaroid hero, illustrated pastel route map, featured destinations grid, Pinterest-style stories grid, journal highlight quotes, masonry photo essay, creator notes, newsletter and the full-screen editorial article view. Do NOT regenerate from scratch.',
  },
  {
    id: 'admin-dashboard',
    type: 'admin-dashboard',
    emoji: '📊',
    title: 'Admin Dashboard',
    desc: 'Enterprise command center with analytics, operations pipeline, and executive KPIs.',
    category: 'Enterprise',
    stack: 'React + Tailwind',
    previewImage: adminDashSnap,
    prompt:
      'Customize this MERIDIAN enterprise operations dashboard template. Edit existing components — CommandBar (top), grouped NavRail (left), SubHeader filters, Executive summary hero, Analytics row (revenue area chart + segments), Operations row (radial gauges + workflow table), Financial row (donut + forecast + cash position), Team row, Smart Operations pipeline, Activity timeline, AI Insights rail (right) and footer. Do NOT regenerate from scratch.',
  },
];

function TemplateCardMedia({ entry, mode }) {
  const [imageFailed, setImageFailed] = useState(false);

  if (!entry.previewImage || imageFailed) {
    return <TemplatePlaceholder emoji={entry.emoji} title={entry.title} mode={mode} />;
  }

  return (
    <img
      src={entry.previewImage}
      alt=""
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
}

function TemplatePlaceholder({ emoji, title, mode }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        background: mode === 'light'
          ? 'linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 50%, #f8fafc 100%)'
          : 'linear-gradient(145deg, #1a1a2e 0%, #161628 50%, #0f0f1a 100%)',
      }}
      aria-hidden
    >
      <span style={{ fontSize: 36, opacity: 0.85 }}>{emoji}</span>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        Preview coming soon
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', opacity: 0.8 }}>{title}</span>
    </div>
  );
}

function TemplateCardSkeleton({ mode }) {
  const isLight = mode === 'light';
  return (
    <div
      style={{
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${isLight ? 'rgba(99,102,241,0.14)' : 'rgba(148,163,184,0.09)'}`,
        background: isLight ? '#ffffff' : 'linear-gradient(155deg, #13162e 0%, #0e1128 100%)',
        boxShadow: isLight
          ? '0 1px 3px rgba(15,23,42,0.07), 0 4px 14px rgba(15,23,42,0.05)'
          : '0 1px 2px rgba(0,0,0,0.5), 0 4px 14px rgba(0,0,0,0.3)',
      }}
    >
      <div
        style={{
          aspectRatio: '16 / 7',
          background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
          animation: 'tg-skeleton-pulse 1.5s ease-in-out infinite',
        }}
      />
      <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 7, borderTop: `1px solid ${isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.07)'}` }}>
        <div style={{ height: 12, width: '60%', borderRadius: 6, background: isLight ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.08)' }} />
        <div style={{ height: 8, width: '90%', borderRadius: 5, background: isLight ? 'rgba(99,102,241,0.05)' : 'rgba(255,255,255,0.05)' }} />
        <div style={{ height: 8, width: '70%', borderRadius: 5, background: isLight ? 'rgba(99,102,241,0.05)' : 'rgba(255,255,255,0.05)' }} />
        <div style={{ height: 7, width: '35%', borderRadius: 5, background: isLight ? 'rgba(99,102,241,0.05)' : 'rgba(255,255,255,0.04)' }} />
      </div>
    </div>
  );
}

export default function TemplateGallery({ onUseTemplate, onNewProject }) {
  const { mode } = useTheme();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const templates = TEMPLATES;

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(t);
  }, []);

  const filtered = templates.filter(entry => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      entry.title.toLowerCase().includes(q) ||
      entry.desc.toLowerCase().includes(q) ||
      entry.category.toLowerCase().includes(q) ||
      entry.stack.toLowerCase().includes(q)
    );
  });

  const s = getStyles(mode);

  const handleUse = (entry) => {
    onUseTemplate(entry.title, entry.prompt);
    setSelected(null);
  };

  return (
    <>
      <style>{`
        @keyframes tg-skeleton-pulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 0.85; }
        }

        /* ── Template gallery workspace layout ── */
        .pl-projects-workspace {
          width: 100%;
          display: flex;
          flex-direction: column;
        }
        .pl-projects-workspace__ambient { display: none; }

        /* outer panel */
        .tg-section-wrap {
          width: 100%;
          background: #0d1021;
          border: 1px solid rgba(99,102,241,0.18);
          border-radius: 24px;
          padding: 32px 32px 36px;
          box-shadow: 0 0 0 1px rgba(99,102,241,0.06), 0 8px 48px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.05);
          display: flex;
          flex-direction: column;
          gap: 0;
          position: relative;
          overflow: hidden;
        }
        [data-theme="light"] .tg-section-wrap {
          background: #f7f6fd;
          border-color: rgba(99,102,241,0.14);
          box-shadow: 0 0 0 1px rgba(99,102,241,0.06), 0 4px 32px rgba(99,102,241,0.06), 0 1px 4px rgba(15,23,42,0.04);
        }

        .pl-projects-container {
          width: 100%;
        }
        .pl-projects-container--header {
          padding: 0;
        }
        .pl-projects-container--grid {
          padding: 20px 0 0;
        }
        .pl-projects-body {
          width: 100%;
        }

        /* ── Templates header ── */
        .pl-projects-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          padding-bottom: 24px;
          border-bottom: 1px solid rgba(99,102,241,0.14);
        }
        [data-theme="light"] .pl-projects-header {
          border-bottom-color: rgba(99,102,241,0.12);
        }
        .pl-projects-header__lead {
          display: flex;
          flex-direction: column;
          gap: 5px;
          min-width: 0;
        }
        /* eyebrow */
        .tg-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(99,102,241,0.7);
        }
        .tg-eyebrow-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          box-shadow: 0 0 6px rgba(99,102,241,0.7);
          flex-shrink: 0;
        }
        /* gradient title */
        .tg-heading-title {
          font-size: 26px;
          font-weight: 800;
          margin: 0;
          letter-spacing: -0.04em;
          line-height: 1.1;
          background: linear-gradient(120deg, #e2e8f0 0%, #c7d2fe 45%, #a5b4fc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        /* tagline */
        .tg-tagline {
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.04em;
          color: rgba(148,163,184,0.6);
          margin: 0;
        }
        .tg-tagline span { color: rgba(165,180,252,0.55); }
        /* count pill */
        .tg-count {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          align-self: flex-start;
          padding: 3px 10px 3px 7px;
          background: rgba(99,102,241,0.1);
          border: 1px solid rgba(99,102,241,0.2);
          border-radius: 20px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.05em;
          color: rgba(165,180,252,0.8);
          text-transform: uppercase;
        }
        .tg-count-pip {
          width: 4px; height: 4px;
          border-radius: 50%;
          background: #818cf8;
          opacity: 0.8;
        }
        /* light theme overrides */
        [data-theme="light"] .tg-eyebrow { color: rgba(79,70,229,0.65); }
        [data-theme="light"] .tg-heading-title {
          background: linear-gradient(120deg, #0f172a 0%, #3730a3 50%, #4f46e5 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        [data-theme="light"] .tg-tagline { color: rgba(71,85,105,0.7); }
        [data-theme="light"] .tg-tagline span { color: rgba(79,70,229,0.5); }
        [data-theme="light"] .tg-count {
          background: rgba(99,102,241,0.07);
          border-color: rgba(99,102,241,0.16);
          color: rgba(79,70,229,0.75);
        }
        [data-theme="light"] .tg-count-pip { background: #6366f1; }
        .pl-projects-header__actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
          flex-wrap: wrap;
          padding-top: 4px;
        }

        /* ── Search field ── */
        .pl-projects-search-wrap {
          position: relative;
          display: flex;
          align-items: center;
          height: 38px;
          border-radius: 11px;
          background: rgba(12,15,30,0.82);
          border: 1px solid rgba(99,102,241,0.18);
          box-shadow: 0 1px 6px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04);
          transition: border-color 0.22s ease, box-shadow 0.22s ease;
          min-width: 220px;
        }
        .pl-projects-search-wrap:focus-within {
          border-color: rgba(99,102,241,0.55);
          box-shadow: 0 0 0 3px rgba(99,102,241,0.12), 0 2px 12px rgba(99,102,241,0.15);
        }
        .pl-projects-search-wrap:hover:not(:focus-within) {
          border-color: rgba(99,102,241,0.35);
          box-shadow: 0 1px 8px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05);
        }
        /* SVG icon — 14×14, absolutely centred on the left */
        .pl-projects-search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 14px;
          height: 14px;
          color: rgba(129,140,248,0.4);
          pointer-events: none;
          flex-shrink: 0;
          transition: color 0.2s ease;
        }
        .pl-projects-search-wrap:focus-within .pl-projects-search-icon {
          color: rgba(129,140,248,0.95);
        }
        .pl-projects-search {
          height: 100% !important;
          padding: 0 12px 0 36px !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
          color: #dde3f0 !important;
          font-size: 13px !important;
          font-family: inherit !important;
          width: 220px;
          letter-spacing: 0.01em;
        }
        .pl-projects-search::placeholder { color: rgba(148,163,184,0.28); }
        /* light theme */
        [data-theme="light"] .pl-projects-search-wrap {
          background: #ffffff;
          border-color: rgba(139,92,246,0.18);
          box-shadow: 0 1px 4px rgba(139,92,246,0.08), inset 0 1px 0 rgba(255,255,255,1);
        }
        [data-theme="light"] .pl-projects-search-wrap:focus-within {
          border-color: rgba(99,102,241,0.45);
          box-shadow: 0 0 0 3px rgba(99,102,241,0.09), 0 2px 10px rgba(139,92,246,0.1);
        }
        [data-theme="light"] .pl-projects-search-wrap:hover:not(:focus-within) {
          border-color: rgba(139,92,246,0.3);
          box-shadow: 0 1px 6px rgba(139,92,246,0.1), inset 0 1px 0 rgba(255,255,255,1);
        }
        [data-theme="light"] .pl-projects-search-icon { color: rgba(99,102,241,0.35); }
        [data-theme="light"] .pl-projects-search-wrap:focus-within .pl-projects-search-icon { color: rgba(79,70,229,0.85); }
        [data-theme="light"] .pl-projects-search { color: #1a1f36 !important; }
        [data-theme="light"] .pl-projects-search::placeholder { color: rgba(100,116,139,0.4); }

        /* ── Create From Scratch button ── */
        .pl-new-project-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0 17px;
          height: 38px;
          background: linear-gradient(135deg, #5b5ef4 0%, #7c3aed 55%, #9333ea 100%);
          border: 1px solid rgba(167,139,250,0.4);
          border-radius: 11px;
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.015em;
          cursor: pointer;
          white-space: nowrap;
          font-family: inherit;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.12),
            inset 0 -1px 0 rgba(0,0,0,0.15),
            0 3px 14px rgba(99,102,241,0.38),
            0 1px 4px rgba(0,0,0,0.22);
          transition:
            transform 0.17s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.17s ease,
            filter 0.17s ease,
            border-color 0.17s ease;
        }
        /* top glass sheen */
        .pl-new-project-btn::after {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 46%;
          background: linear-gradient(180deg, rgba(255,255,255,0.16) 0%, transparent 100%);
          border-radius: 11px 11px 0 0;
          pointer-events: none;
        }
        .pl-new-project-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.1);
          border-color: rgba(192,168,255,0.55);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.14),
            inset 0 -1px 0 rgba(0,0,0,0.15),
            0 6px 24px rgba(99,102,241,0.52),
            0 2px 6px rgba(0,0,0,0.24);
        }
        .pl-new-project-btn:active {
          transform: translateY(0) scale(0.985);
          filter: brightness(0.96);
          box-shadow: 0 1px 6px rgba(99,102,241,0.28), 0 1px 3px rgba(0,0,0,0.2);
        }
        [data-theme="light"] .pl-new-project-btn {
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.22),
            inset 0 -1px 0 rgba(0,0,0,0.08),
            0 2px 12px rgba(99,102,241,0.28),
            0 1px 3px rgba(99,102,241,0.12);
        }
        [data-theme="light"] .pl-new-project-btn:hover {
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.24),
            inset 0 -1px 0 rgba(0,0,0,0.08),
            0 6px 22px rgba(99,102,241,0.38),
            0 2px 6px rgba(99,102,241,0.14);
        }

        /* ── Responsive ── */
        @media (max-width: 640px) {
          .pl-projects-header { flex-direction: column; align-items: stretch; }
          .pl-projects-header__actions { width: 100%; }
          .pl-projects-search-wrap { min-width: 0; flex: 1; }
          .pl-projects-search { width: 100%; }
        }

        /* ── Template card — Project card visual language ── */
        .tg-card-root {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(148,163,184,0.09);
          background: linear-gradient(155deg, #13162e 0%, #0e1128 100%);
          box-shadow: 0 1px 2px rgba(0,0,0,0.5), 0 4px 14px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05);
          outline: none;
          max-width: 100%;
          cursor: pointer;
          text-align: left;
          width: 100%;
          padding: 0;
          transition:
            transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 0.22s ease,
            border-color 0.22s ease,
            background 0.22s ease;
        }
        /* 2px accent bar across top — matches pl-project-card::before */
        .tg-card-root::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, rgba(99,102,241,0.5) 0%, rgba(139,92,246,0.35) 100%);
          border-radius: 16px 16px 0 0;
          pointer-events: none;
          opacity: 0.6;
          transition: opacity 0.22s ease;
          z-index: 1;
        }
        .tg-card-root:hover::before { opacity: 1; }
        .tg-card-root:hover {
          border-color: rgba(148,163,184,0.16);
          background: linear-gradient(155deg, #161a36 0%, #111428 100%);
          transform: translateY(-3px);
          box-shadow: 0 2px 6px rgba(0,0,0,0.4), 0 8px 22px rgba(0,0,0,0.28), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.07);
        }
        .tg-card-root:focus-visible {
          box-shadow: 0 0 0 2px rgba(99,102,241,0.5), 0 4px 14px rgba(0,0,0,0.3);
        }
        /* light theme */
        [data-theme="light"] .tg-card-root {
          background: #ffffff;
          border-color: rgba(99,102,241,0.14);
          box-shadow: 0 1px 3px rgba(15,23,42,0.07), 0 4px 14px rgba(15,23,42,0.05), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        [data-theme="light"] .tg-card-root::before {
          background: linear-gradient(90deg, rgba(99,102,241,0.4) 0%, rgba(139,92,246,0.28) 100%);
        }
        [data-theme="light"] .tg-card-root:hover {
          background: #fafafe;
          border-color: rgba(99,102,241,0.26);
          box-shadow: 0 2px 6px rgba(15,23,42,0.08), 0 8px 20px rgba(99,102,241,0.07), 0 0 0 1px rgba(99,102,241,0.07), inset 0 1px 0 rgba(255,255,255,0.9);
        }

        .tg-card-media {
          position: relative;
          aspect-ratio: 16 / 9;
          overflow: hidden;
          display: block;
          padding: 0;
          margin: 0;
          line-height: 0;
          box-sizing: border-box;
          background: #0b0e1c;
          border-radius: 16px 16px 0 0;
        }
        [data-theme="light"] .tg-card-media {
          background: #eef0f8;
        }
        .tg-card-media img {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          border: 0;
          object-fit: cover;
          object-position: top center;
          display: block;
          transform: scale(1.0);
          transform-origin: center center;
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
        }
        .tg-card-root:hover .tg-card-media img {
          transform: scale(1.03);
        }
        /* subtle bottom-fade overlay — softens the image-to-footer edge */
        .tg-card-media::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 40%;
          background: linear-gradient(to bottom, transparent 0%, rgba(10,12,28,0.45) 100%);
          pointer-events: none;
          transition: opacity 0.3s ease;
        }
        [data-theme="light"] .tg-card-media::after {
          background: linear-gradient(to bottom, transparent 0%, rgba(15,23,42,0.18) 100%);
        }
        .tg-card-root:hover .tg-card-media::after {
          opacity: 0.7;
        }
        .tg-template-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(272px, 1fr));
          gap: 14px;
          width: 100%;
          max-width: 100%;
        }
        @media (max-width: 600px) {
          .tg-template-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="pl-projects-workspace">
        <div className="pl-projects-workspace__ambient" aria-hidden />

        <div className="tg-section-wrap">

        <div className="pl-projects-container pl-projects-container--header">
          <header className="pl-projects-header">
            <div className="pl-projects-header__lead">
              <span className="tg-eyebrow">
                <span className="tg-eyebrow-dot" />
                Pre-built Solutions
              </span>
              <h1 className="tg-heading-title">Templates</h1>
              <p className="tg-tagline">
                Start faster<span> · </span>Customise freely<span> · </span>Ship sooner
              </p>
              {!loading && (
                <span className="tg-count">
                  <span className="tg-count-pip" />
                  {templates.length} template{templates.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="pl-projects-header__actions">
              <div className="pl-projects-search-wrap">
                <svg className="pl-projects-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="6.5" cy="6.5" r="4"/>
                  <line x1="9.8" y1="9.8" x2="14" y2="14"/>
                </svg>
                <input
                  className="pl-search pl-projects-search"
                  placeholder="Search templates..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <button type="button" className="pl-new-project-btn" onClick={onNewProject}>
                + Create From Scratch
              </button>
            </div>
          </header>
        </div>

        <div className="pl-projects-container pl-projects-container--grid">
          <div className="pl-projects-body">
            {loading ? (
              <div className="tg-template-grid">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i}>
                    <TemplateCardSkeleton mode={mode} />
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={s.emptyState}>
                <span style={{ fontSize: 40, opacity: 0.25 }}>📦</span>
                <p style={s.emptyTitle}>
                  {search ? `No templates matching "${search}"` : 'No templates available'}
                </p>
                <p style={s.emptySub}>
                  {search ? 'Try a different search term' : 'Check back later or create a project from scratch'}
                </p>
                {!search && onNewProject && (
                  <button type="button" className="pl-new-project-btn" style={{ marginTop: 8 }} onClick={onNewProject}>
                    + Create From Scratch
                  </button>
                )}
              </div>
            ) : (
              <div className="tg-template-grid" role="list">
                {filtered.map(entry => (
                  <div key={entry.id} role="listitem">
                    <article
                      role="button"
                      tabIndex={0}
                      className="tg-card-root"
                      aria-label={`Preview ${entry.title} template`}
                      onClick={() => setSelected(entry)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelected(entry);
                        }
                      }}
                    >
                      <div className="tg-card-media">
                        <TemplateCardMedia entry={entry} mode={mode} />
                      </div>
                      <div style={s.cardFooter}>
                        <div style={s.cardFooterTop}>
                          <div style={s.cardFooterText}>
                            <h3 style={s.cardTitle}>{entry.title}</h3>
                          </div>
                          <span style={s.categoryBadge}>{entry.category}</span>
                        </div>
                        <p style={s.cardDesc}>{entry.desc}</p>
                        <div style={s.cardFooterMeta}>
                          <span style={s.cardStack}>{entry.stack}</span>
                        </div>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        </div>{/* /tg-section-wrap */}
      </div>

      {selected && (
        <TemplatePreviewModal
          template={selected}
          onClose={() => setSelected(null)}
          onUse={(t) => {
            const entry = templates.find(x => x.title === t.title);
            if (entry) handleUse(entry);
            else onUseTemplate(t.title, t.prompt);
            setSelected(null);
          }}
        />
      )}
    </>
  );
}

const getStyles = (mode) => ({
  cardFooter: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '12px 14px 14px',
    borderTop: `1px solid ${mode === 'light' ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.07)'}`,
  },
  cardFooterTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardFooterText: { minWidth: 0, flex: 1 },
  cardTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: mode === 'light' ? '#111827' : '#e8edf8',
    margin: 0,
    lineHeight: 1.3,
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardDesc: {
    fontSize: 11,
    color: mode === 'light' ? 'rgba(71,85,105,0.75)' : 'rgba(148,163,184,0.55)',
    margin: '4px 0 0',
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardFooterMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  cardDot: {
    width: 3,
    height: 3,
    borderRadius: '50%',
    background: mode === 'light' ? 'rgba(100,116,139,0.35)' : 'rgba(148,163,184,0.32)',
    flexShrink: 0,
  },
  cardStack: {
    fontSize: 10,
    fontWeight: 500,
    color: mode === 'light' ? 'rgba(71,85,105,0.58)' : 'rgba(148,163,184,0.45)',
    letterSpacing: '0.01em',
  },
  categoryBadge: {
    flexShrink: 0,
    fontSize: 9,
    fontWeight: 700,
    color: mode === 'light' ? '#4f46e5' : '#a5b4fc',
    background: 'rgba(99,102,241,0.12)',
    border: '1px solid rgba(99,102,241,0.22)',
    padding: '3px 8px',
    borderRadius: 6,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '32px 24px',
    textAlign: 'center',
  },
  emptyTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 },
  emptySub: { fontSize: 13, color: 'var(--text-muted)', margin: 0, maxWidth: 360 },
});
